import React, { Component } from "react";
import { withRouter, Link } from "react-router";
import { AutoComplete, Input, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { getQuickSearchResult } from "../../services/api-quick-search";
import { API_URL } from "../../config";
import { isArray } from "lodash";
import { toTitleCase } from "../../helpers/converters";

const toEntityString = val => (isArray(val) ? val[0] ? `${val[0]} et al.` : "N/A" : val || "N/A");
const toAdvancedSearchLink = (title, path) => (
  <span>
    {title}
    <Link to={path} style={{ float: "right" }}>
      Advanced Search
    </Link>
  </span>
);

const toSearch = [
  {
    id: "ptabCase",
    esUrl: `${API_URL}/cases/ptab/_search`,
    fields: ["case_number", "petitioners", "patent_owners", "patent.patent_number"],
    searchType: "doc",
    title: toAdvancedSearchLink("PTAB Case", "/ptab/caselist?sort=-filing_date"),
    renderItem: item => (
      <div>
        <b>{`${item.case_number} `}</b>
        {`${toEntityString(item.petitioners)} v. ${toEntityString(item.patent_owners)}`}
      </div>
    ),
    getItemLink: item => `/ptab/case/${item.case_number}`
  },
  {
    id: "litigationCase",
    esUrl: `${API_URL}/cases/cafc-ln,cfc-ln,dc-ln,itc-ln,sc-ln/_search`,
    fields: ["case_no", "plaintiff", "defendant", "court", "patents"],
    searchType: "doc",
    title: toAdvancedSearchLink("Litigation Case", "/litigation/caselist?sort=-filed_date"),
    renderItem: item => (
      <div>
        <b>{`${item.case_no} `}</b>
        {`${toEntityString(item.plaintiff)} v. ${toEntityString(item.defendant)}`}
      </div>
    ),
    getItemLink: item => `/litigation/${item.court}/case/${item.case_no}`
  },
  {
    id: "reexamCase",
    esUrl: `${API_URL}/patents/prosecution/_search`,
    fields: ["application_number", "requester", "owner", "patent_number"],
    extraQuery: { series_code: 90 },
    searchType: "doc",
    title: toAdvancedSearchLink("Ex-Parte Re-exams", "/exparte/search"),
    renderItem: item => (
      <div>
        <b>{`${item.application_number} `}</b>
        {item.patent_number ? `Re-exam of ${item.patent_number} ` : ""}
        {`${toEntityString(item.requester)} v. ${toEntityString(item.owner)}`}
      </div>
    ),
    getItemLink: item => `/exparte/${item.application_number}`
  },
  {
    id: "upcCase",
    esUrl: `${API_URL}/cases/upc/_search`,
    fields: ["case_number", "claimant", "respondent", "patent_number"],
    searchType: "doc",
    title: toAdvancedSearchLink("UPC Case", "/upc/search?sort=-filing_date"),
    renderItem: item => (
      <div>
        <b>{`${item.case_number} `}</b>
        {`${toEntityString(item.claimant)} v. ${toEntityString(item.respondent)}`}
      </div>
    ),
    getItemLink: item => `/upc/case/${encodeURIComponent(item.case_number)}`
  },
  {
    id: "patentNum",
    esUrl: `${API_URL}/patents/v6/_search`,
    fields: ["publication_number"],
    searchType: "doc",
    title: toAdvancedSearchLink("Patents", "/patents/search"),
    renderItem: item => <div>{item.publication_number}</div>,
    getItemLink: item => `/patents/patent/${item.publication_number}`
  },
  {
    id: "ptabJudge",
    esUrl: `${API_URL}/cases/ptab/_search`,
    fields: ["judges.panel"],
    searchType: "field",
    title: "PTAB Judges",
    renderItem: item => <b>{toTitleCase(item)}</b>,
    getItemLink: item => `/ptab/judge/${toTitleCase(item)}`
  }
];

class QuickSearch extends Component {
  state = {
    value: "",
    options: [],
  };

  componentDidMount() {
    this._isMounted = true;
    this._timeout = 0;
  }

  render() {
    const { value, options } = this.state;
    return (
      <AutoComplete
        value={value}
        onSearch={this._onSearch}
        onSelect={this._onSelect}
        options={options}
        defaultActiveFirstOption={true}
        style={this.props.style}
        className={this.props.className}
      >
        <Input suffix={<SearchOutlined />} placeholder="Quick Search ..." size="large" />
      </AutoComplete>
    );
  }

  _onSearch = val => {
    if (this._isMounted) this.setState({ value: val });
    if (this._timeout) clearTimeout(this._timeout);
    this._timeout = setTimeout(() => {
      this._searchCompletion(val);
    }, 800);
  };

  _onSelect = val => {
    if (val) {
      this.props.router.push(val);
    }
    if (this._isMounted) this.setState({ value: "", options: [] });
  };

  _fetchSectionResult = (section, value) => getQuickSearchResult(section.searchType, section.esUrl, section.fields, value, section.extraQuery)
    .then(res => {
      if (this._isMounted) this.setState(prevState => ({
        options: prevState.options
          .map(option => {
            if (option.id === section.id) {
              const optionExtra = res.length > 0
                ? {
                  state: "hasResults",
                  options: res.sort(section.sort).map(h => ({
                    value: section.getItemLink(h),
                    label: section.renderItem(h)
                  }))
                }
                : {
                  state: "noResults",
                  options: [{
                    value: `${section.id}_no_results`,
                    label: "No results found",
                    disabled: true
                  }]
                };
              return { ...option, ...optionExtra }
            }
            return option;
          })
          // order section by state, noResults at the bottom, others stay as is
          .sort((a, b) => {
            if (a.state === "noResults" && b.state !== "noResults") return 1;
            if (a.state !== "noResults" && b.state === "noResults") return -1;
            return 0;
          })
      }));
    })
    .catch(() => {
      if (this._isMounted) this.setState(prevState => ({
        options: prevState.options.map(option => option.id === section.id ? { ...option, options: [] } : option)
      }));
    })

  _searchCompletion = val => {
    const value = (val || "").trim().replace(/,/g, "").replace(/^US-?/, "");
    if (value) {
      // initialize loading options when searching non-empty input
      // after initialization is done, update search result of each section incrementally
      if (this._isMounted) this.setState({
        options: toSearch.map(section => ({
          label: section.title,
          id: section.id,
          state: "loading",
          options: [{
            value: `${section.id}_loading`,
            label: <Spin />,
            disabled: true
          }]
        }))
      }, () => toSearch.forEach(section => this._fetchSectionResult(section, value)));
    } else {
      // clear options when input is empty
      if (this._isMounted) this.setState({ options: [] });
    }
  };
}

export default withRouter(QuickSearch);
